import { DEFAULT_USER_LOCALE, REGIONS_WITH_LOCALES } from "@client-shared/lib/constants";
import * as T from "@shared/types";
import { Locale } from "@shared/types";
import React from "react";
import * as api from '@client-shared/services/api';
import { TranslationCategoriesMap, LocaleWithLocalisedProductConfig, LocaleWithPimProductsCategories } from "./types";

export const translationCategoriesMap: TranslationCategoriesMap = {
  "BEAN-COFFEE-STYLE": "brewStyle",
  "BEAN-TYPE": "productType",
  "BREW-METHOD": "brewMethod",
  CERTIFICATION: "certifications",
  COUNTRIES: "country",
  "DISCOVERY-FLAVOUR-CATEGORY": "secondaryFlavourCategories",
  "FLAVOUR-CATEGORY": "flavorCategories",
  "GRIND-TYPE": "grind",
  PROCESS: "process",
  REGION: "region",
  "ROAST-LEVEL": "roastLevel",
  "SECONDARY-FLAVOUR-CATEGORY": "secondaryFlavourCategories",
  "TASTING-NOTES": "tastingNotes",
  VARIETAL: "varietal",
  TERM: "term",
};

export const categoriesKeys: T.TranslationCategory[] = [
  "BEAN-COFFEE-STYLE",
  "BEAN-TYPE",
  "BREW-METHOD",
  "CERTIFICATION",
  "COUNTRIES",
  "DISCOVERY-FLAVOUR-CATEGORY",
  "FLAVOUR-CATEGORY",
  "GRIND-TYPE",
  "PROCESS",
  "REGION",
  "ROAST-LEVEL",
  "SECONDARY-FLAVOUR-CATEGORY",
  "TASTING-NOTES",
  "TERM",
  "VARIETAL",
];

export const setOptionsToCountries = (
  productConfig: T.ProductConfig,
  productCats: T.PimProductsCategories
): T.CountriesOptionsByRegionKey => {
  const { regionAndCountry } = productConfig;
  const keys = Object.keys(regionAndCountry);
  const regionsWithCountries = keys.reduce((acc, key) => {
    const countries = regionAndCountry[key] as string[];
    const countryOptions = productCats?.COUNTRIES?.filter((item) => countries.includes(item.keyName)).map((item) => ({
      label: item.translatedValue,
      value: item.keyName,
    }));
    return {
      ...acc,
      [key]: countryOptions,
    };
  }, {});
  return regionsWithCountries;
};

export const transformCategoriesToLocalisedProductConfig = (response: T.PimProductsCategories) => {
  if (!response) return;
  if (typeof response !== "object") return;
  const keys = Object.keys(response);
  if (keys.length === 0) return;

  const setConfig = keys.reduce((acc, key) => {
    if (!translationCategoriesMap[key as T.TranslationCategory]) return { ...acc };
    return {
      ...acc,
      [translationCategoriesMap[key as T.TranslationCategory]]: response[key]?.map((item) => ({
        label: item.translatedValue,
        value: item.keyName,
      })),
    };
  }, {});
  return setConfig as T.LocalisedProductConfig;
};

// config for a single form

export const fetchSingleLocalisedFormConfig = async (locale: Locale) => {
  const response = await api.getLocalisedProductConfig(locale);
  return response;
};

export const setLocalisedProductConfigByLocale = async (locale: Locale): Promise<LocaleWithPimProductsCategories> => {
  const singleFormConfig = await fetchSingleLocalisedFormConfig(locale);
  return { [locale]: singleFormConfig };
};

export const setToConfig = (productCategories: T.PimProductsCategories, productConfig: T.ProductConfig) => {
  const setConfig = transformCategoriesToLocalisedProductConfig(productCategories);
  if (!productConfig || !setConfig) return;
  const countriesByRegion = setOptionsToCountries(productConfig, productCategories);
  return { ...setConfig, regionAndCountry: countriesByRegion };
};

export const setCategoriesToOptions = (localeWithPimProductsCategory: LocaleWithPimProductsCategories, productConfig: T.ProductConfig) => {
  const locale = Object.keys(localeWithPimProductsCategory)[0] as Locale;
  const productCategories = Object.values(localeWithPimProductsCategory)[0] as T.PimProductsCategories;
  return { [locale]: setToConfig(productCategories, productConfig) };
};

export const fetchAllRegionFormConfigs = async (locales: Locale[]) => {
  const allLocalisedProductConfigByLocale = Promise.all(locales.map(async (locale) => await setLocalisedProductConfigByLocale(locale)));
  await allLocalisedProductConfigByLocale;
  return allLocalisedProductConfigByLocale;
};

export const setAllLocalisedProductConfigByLocale = async (locales: Locale[], productConfig: T.ProductConfig) => {
  const allLocalisedProductConfigByLocale = await fetchAllRegionFormConfigs(locales);
  return allLocalisedProductConfigByLocale.map((localeWithPimProductsCategory) =>
    setCategoriesToOptions(localeWithPimProductsCategory, productConfig)
  );
};

export const mergeRegionsFormsConfig = (localisedProductConfigByLocalesArray: LocaleWithLocalisedProductConfig[]) => {
  return localisedProductConfigByLocalesArray.reduce((acc, obj) => {
    return { ...acc, ...obj };
  }, {});
};

export const useLocalisedProductConfig = (locales: Locale[] | undefined, productConfig: T.ProductConfig | undefined) => {
  const [localisedProductConfigByLocale, setLocalisedProductConfigByLocale] = React.useState<LocaleWithLocalisedProductConfig>();
  React.useEffect(() => {
    if (!productConfig || !locales) return;
    const fetchAllRegionFormConfigs = async () => {
      const localisedProductFormConfigs = await setAllLocalisedProductConfigByLocale(locales, productConfig);
      if (!localisedProductFormConfigs) return;
      setLocalisedProductConfigByLocale(mergeRegionsFormsConfig(localisedProductFormConfigs));
    };
    fetchAllRegionFormConfigs();
  }, [locales, productConfig]);
  return { localisedProductConfigByLocale };
};

export const getLocalesWithLocalisedProductConfig = async (locales: Locale[], productConfig: T.ProductConfig) => {
  const localisedProductFormConfigs = await setAllLocalisedProductConfigByLocale(locales, productConfig);
  return mergeRegionsFormsConfig(localisedProductFormConfigs);
};

export const setDefaultRegionLocalizedProductConfig = (
  localisedProductConfigByLocale?: T.LocaleWithLocalisedProductConfig,
  region?: string
) => {
  if (!localisedProductConfigByLocale || !region) return undefined;
  const defaultLocale =
    REGIONS_WITH_LOCALES[region as keyof typeof REGIONS_WITH_LOCALES].find((obj) => obj.isDefault)?.locale ?? DEFAULT_USER_LOCALE;
  return localisedProductConfigByLocale[defaultLocale];
};
